// App: Main application logic and initialization
import { EditorManager } from './modules/EditorManager';
import { TabManager } from './modules/TabManager';
import { FileManager } from './modules/FileManager';
import { PersistenceManager } from './modules/PersistenceManager';
import { Toolbar } from './ui/Toolbar';
import { TabBar } from './ui/TabBar';
import { StatusBar } from './ui/StatusBar';
import { KeyboardShortcutsDialog } from './ui/KeyboardShortcutsDialog';
import { detectLanguageFromFileName } from './utils/language-detector';
import { debounce } from './utils/debounce';
import { EditorConfig } from './modules/types';

export class App {
  private editorManager!: EditorManager;
  private tabManager!: TabManager;
  private fileManager!: FileManager;
  private persistenceManager!: PersistenceManager;
  private toolbar!: Toolbar;
  private tabBar!: TabBar;
  private statusBar!: StatusBar;
  private keyboardShortcutsDialog!: KeyboardShortcutsDialog;
  private currentTheme: 'vs' | 'vs-dark' = 'vs-dark';
  private debouncedAutoSave!: (tabId: string, content: string) => void;

  async init(): Promise<void> {
    // Initialize managers
    this.persistenceManager = new PersistenceManager();
    await this.persistenceManager.init();

    this.tabManager = new TabManager(this.persistenceManager);
    this.fileManager = new FileManager();
    this.editorManager = new EditorManager();

    // Initialize UI components
    this.toolbar = new Toolbar(document.getElementById('toolbar')!);
    this.tabBar = new TabBar(document.getElementById('tab-bar')!);
    this.statusBar = new StatusBar(document.getElementById('status-bar')!);
    this.keyboardShortcutsDialog = new KeyboardShortcutsDialog();

    // Initialize editor
    const editorContainer = document.getElementById('editor-container')!;
    const config: EditorConfig = {
      theme: this.currentTheme,
      fontSize: 14,
      tabSize: 2,
      insertSpaces: true,
      lineNumbers: 'on',
      minimap: { enabled: false },
      wordWrap: 'off',
    };
    await this.editorManager.init(editorContainer, config);

    // Set up debounced auto-save
    this.debouncedAutoSave = debounce((tabId: string, content: string) => {
      this.tabManager.updateTabContent(tabId, content);
    }, 1000);

    // Bind events
    this.bindEvents();

    // Restore tabs from storage
    await this.tabManager.restoreTabs();

    // If no tabs, create a default one
    if (this.tabManager.tabs.length === 0) {
      this.createNewTab();
    } else {
      this.syncEditorWithActiveTab();
    }

    this.render();

    // Set up drag-and-drop
    this.setupDragAndDrop();

    // Set up keyboard shortcuts
    this.setupKeyboardShortcuts();
  }

  private bindEvents(): void {
    // Toolbar events
    this.toolbar.on('new', () => this.createNewTab());
    this.toolbar.on('open', () => this.openFile());
    this.toolbar.on('save', () => this.saveFile());
    this.toolbar.on('saveAs', () => this.saveFileAs());
    this.toolbar.on('toggleTheme', () => this.toggleTheme());
    this.toolbar.on('settings', () => this.showSettings());
    this.toolbar.on('help', () => this.showKeyboardShortcuts());

    // TabBar events
    this.tabBar.on('tabSwitch', (tabId) => this.switchTab(tabId));
    this.tabBar.on('tabClose', (tabId) => this.closeTab(tabId));
    this.tabBar.on('newTab', () => this.createNewTab());

    // Editor events
    this.editorManager.onContentChange((content) => {
      const activeTab = this.tabManager.getActiveTab();
      if (activeTab) {
        this.debouncedAutoSave(activeTab.id, content);
      }
    });

    this.editorManager.onPositionChange((line, column) => {
      this.statusBar.updatePosition(line, column);
    });

    // StatusBar events
    this.statusBar.onLanguageChange((language) => this.changeLanguage(language));

    // TabManager events
    this.tabManager.onChange(() => this.render());
  }

  private createNewTab(): void {
    const untitledCount = this.tabManager.tabs.filter((t) => t.fileName.startsWith('Untitled-')).length;
    const fileName = `Untitled-${untitledCount + 1}.txt`;
    this.tabManager.createTab(fileName, '', 'plaintext');
    this.syncEditorWithActiveTab();
  }

  private async openFile(): Promise<void> {
    try {
      const result = await this.fileManager.openFile();
      const language = detectLanguageFromFileName(result.name);
      const tab = this.tabManager.createTab(result.name, result.content, language, result.handle);
      this.switchTab(tab.id);
    } catch (error: any) {
      if (error.message !== 'File selection cancelled') {
        alert(`Failed to open file: ${error.message}`);
      }
    }
  }

  private async saveFile(): Promise<void> {
    const activeTab = this.tabManager.getActiveTab();
    if (!activeTab) return;

    const content = this.editorManager.getContent();
    
    try {
      if (activeTab.fileHandle) {
        const handle = await this.fileManager.saveFile(content, activeTab.fileHandle);
        if (handle) {
          this.tabManager.markSaved(activeTab.id);
          alert('File saved successfully!');
          return;
        }
      }
      
      // Fallback to Save As if no handle or FSA save failed
      await this.saveFileAs();
    } catch (error: any) {
      alert(`Failed to save file: ${error.message}`);
    }
  }

  private async saveFileAs(): Promise<void> {
    const activeTab = this.tabManager.getActiveTab();
    if (!activeTab) return;

    const content = this.editorManager.getContent();
    
    try {
      const handle = await this.fileManager.saveAsFile(content, activeTab.fileName);
      if (handle) {
        // Update tab with new handle and mark as saved
        activeTab.fileHandle = handle;
        activeTab.fileName = (await handle.getFile()).name;
        this.tabManager.markSaved(activeTab.id);
        this.render();
        alert('File saved successfully!');
      }
    } catch (error: any) {
      alert(`Failed to save file: ${error.message}`);
    }
  }

  private switchTab(tabId: string): void {
    this.tabManager.switchTab(tabId);
    this.syncEditorWithActiveTab();
  }

  private closeTab(tabId: string): void {
    const closed = this.tabManager.closeTab(tabId);
    if (closed) {
      this.syncEditorWithActiveTab();
    }
  }

  private syncEditorWithActiveTab(): void {
    const activeTab = this.tabManager.getActiveTab();
    if (activeTab) {
      this.editorManager.setContent(activeTab.content, activeTab.language);
      this.statusBar.updateFile(activeTab.fileName);
      this.statusBar.updateLanguage(activeTab.language);
      this.toolbar.updateSaveButton(true);
    } else {
      this.editorManager.setContent('', 'plaintext');
      this.statusBar.updateFile('No file open');
      this.statusBar.updateLanguage('Plain Text');
      this.toolbar.updateSaveButton(false);
    }
  }

  private toggleTheme(): void {
    this.currentTheme = this.currentTheme === 'vs-dark' ? 'vs' : 'vs-dark';
    this.editorManager.updateConfig({ theme: this.currentTheme });
    document.body.classList.toggle('dark-theme', this.currentTheme === 'vs-dark');
  }

  private showSettings(): void {
    alert('Settings dialog coming soon!\nFor now, use Ctrl+S to save, Ctrl+O to open, and the Theme button to toggle dark/light mode.');
  }

  private showKeyboardShortcuts(): void {
    this.keyboardShortcutsDialog.show();
  }

  private changeLanguage(language: string): void {
    const activeTab = this.tabManager.getActiveTab();
    if (activeTab) {
      // Update tab language
      activeTab.language = language;
      // Update editor language
      this.editorManager.setLanguage(language);
      // Update status bar
      this.statusBar.updateLanguage(language);
      // Mark as unsaved since language change is metadata change
      this.tabManager.markUnsaved(activeTab.id);
      // Save tabs
      this.tabManager.saveTabs();
    }
  }

  private setupDragAndDrop(): void {
    const editorContainer = document.getElementById('editor-container')!;
    
    editorContainer.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });

    editorContainer.addEventListener('drop', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      try {
        const result = await this.fileManager.handleFileDrop(e);
        const language = detectLanguageFromFileName(result.name);
        const tab = this.tabManager.createTab(result.name, result.content, language, result.handle);
        this.switchTab(tab.id);
      } catch (error: any) {
        alert(`Failed to open dropped file: ${error.message}`);
      }
    });
  }

  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      // Ctrl+N or Cmd+N: New file
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        this.createNewTab();
      }

      // Ctrl+O or Cmd+O: Open file
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        this.openFile();
      }

      // Ctrl+S or Cmd+S: Save file
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && !e.shiftKey) {
        e.preventDefault();
        this.saveFile();
      }

      // Ctrl+Shift+S or Cmd+Shift+S: Save As
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && e.shiftKey) {
        e.preventDefault();
        this.saveFileAs();
      }

      // Ctrl+W or Cmd+W: Close tab
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault();
        const activeTab = this.tabManager.getActiveTab();
        if (activeTab) {
          this.closeTab(activeTab.id);
        }
      }

      // Ctrl+Tab: Next tab
      if (e.ctrlKey && e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        this.switchToNextTab();
      }

      // Ctrl+Shift+Tab: Previous tab
      if (e.ctrlKey && e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        this.switchToPreviousTab();
      }

      // F1 or ?: Show keyboard shortcuts
      if (e.key === 'F1' || (e.shiftKey && e.key === '?')) {
        e.preventDefault();
        this.showKeyboardShortcuts();
      }
    });
  }

  private switchToNextTab(): void {
    const tabs = this.tabManager.tabs;
    if (tabs.length <= 1) return;
    
    const currentIndex = tabs.findIndex((t) => t.id === this.tabManager.activeTabId);
    const nextIndex = (currentIndex + 1) % tabs.length;
    this.switchTab(tabs[nextIndex].id);
  }

  private switchToPreviousTab(): void {
    const tabs = this.tabManager.tabs;
    if (tabs.length <= 1) return;
    
    const currentIndex = tabs.findIndex((t) => t.id === this.tabManager.activeTabId);
    const prevIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
    this.switchTab(tabs[prevIndex].id);
  }

  private render(): void {
    this.tabBar.render(this.tabManager.tabs, this.tabManager.activeTabId);
    
    const activeTab = this.tabManager.getActiveTab();
    if (activeTab) {
      this.statusBar.updateFile(activeTab.fileName);
      this.statusBar.updateLanguage(activeTab.language);
    }
  }
}
